import { useState, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import StatusIndicator from '../../shared/statusIndicator';
import SectionTitle from '../../shared/sectionTitle';
import CycleTimeline from '../../shared/cycleTimeline';
import CycleStepsStats from '../../shared/cycleStepsStats';
import Search from '../../shared/search';
import CollapsableTable from '../../shared/collapsableTable';
import UserAvatar from '../../shared/userAvatar';
import NotificationCard from '../../shared/notificationCard';
import CycleReviewers from '../../shared/cycleReviewers';
import ActionButton from '../../shared/actionButton';
import {
  APP_PAGES,
  PARAMS,
  CYCLE_DETAILS_DEFAULT_PARAMS,
} from '../../../constants/pages';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';
import { api_cycle_user_reviewers } from '../../../constants/apiRoutes';
import { sticky } from '../../../constants/helperCssRules';
import {
  getCycleTimelineStats,
  getCycleEmployees,
  clearCycle,
} from '../../../store/modules/cycleDetails';
import { cycleDetailsSelector } from '../../../store/selectors/cycleDetailsSelector';
import {
  prepareCycleStatuses,
  prepareCycleSteps,
  prepareCycleStepsTimelineAndStats,
  prepareEmployeesTableHeaderActions,
  prepareEmployeesTableHeaders,
  prepareEmployeesTableData,
} from '../../../utility/cycles';
import { prepareSurveyReviewerStatuses } from '../../../utility/survey';
import { useTranslations } from '../../../utility/useTranslations';
import {
  getItemById,
  isArray,
  isArrayEmpty,
  trimString,
} from '../../../utility/helpers';
import { formatDate } from '../../../utility/dateUtils';
import { hasNextPage } from '../../../utility/uiUtils';
import http from '../../../utility/http';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  headerWrapper: {
    maxWidth: 'calc(100% - 260px)',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    maxWidth: '100%',
    width: 'fit-content',
  },
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    marginTop: spacing(1.5),
  },
  dot: {
    backgroundColor: primary.bluish1,
    borderRadius: '50%',
    margin: spacing(0.5, 3, 0, 3),
    width: 6,
    height: 6,
  },
  divider: {
    paddingTop: spacing(8),
    ...sticky(primary.white, 105),
  },
  labelSpacing: {
    marginBottom: spacing(6),
  },
  sectionSpacing: {
    marginBottom: spacing(6),
  },
  employeeSectionTitle: {
    marginBottom: spacing(2),
  },
  employeesTableDescription: {
    marginBottom: spacing(6),
  },
  search: {
    width: 260,
    marginBottom: spacing(6),
  },
  tableHeader: {
    ...sticky(primary.white, 137),
  },
  employeeLabel: {
    fontFamily: 'ProximaNova-Bold',
  },
  employeeTableActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}));

const CycleDetailsPage = ({
  auth,
  accessControl,
  navigate,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.CYCLE_DETAILS_PAGE);
  const { cycleId } = useParams();

  const { stats, employees } = useSelector(cycleDetailsSelector);
  const { title, users_count, start_at, end_at, status, steps } = stats;
  const [page, setPage] = useState(CYCLE_DETAILS_DEFAULT_PARAMS[PARAMS.PAGE]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [search, setSearch] = useState('');

  const START_END_DATE_FORMAT = 'MMMM dd, yyyy';
  const statuses = prepareCycleStatuses(translations.statuses);
  const reviewerStatuses = prepareSurveyReviewerStatuses(translations.statuses);
  const cycleStatus = getItemById(statuses, status);
  const allSteps = prepareCycleSteps(translations.cycleSteps);
  const { timelineStats, hasEmailStep } = prepareCycleStepsTimelineAndStats(
    translations.completionStatuses,
    steps,
    allSteps
  );
  const employeesTableHeaderActions = prepareEmployeesTableHeaderActions(
    translations.columns
  );
  const employeesTableHeaders = prepareEmployeesTableHeaders(
    translations.columns
  );
  const hasLoadMoreEmployees = hasNextPage(employees);

  const handleGetEmployees = ({ newPage, newSearch, isLoadMore } = {}) => {
    const params = {
      [PARAMS.PAGE]: newPage,
      ...(newSearch ? { [PARAMS.SEARCH]: newSearch } : {}),
    };

    return getCycleEmployees(dispatch, cycleId, params, isLoadMore);
  };

  const getCycleData = useCallback(async () => {
    try {
      await Promise.all([
        getCycleTimelineStats(dispatch, cycleId),
        getCycleEmployees(dispatch, cycleId),
      ]);
    } finally {
      setIsInitialLoad(false);
    }
  }, [dispatch, cycleId]);

  const onSearch = text => {
    const searchTerm = trimString(text);

    handleGetEmployees({
      newPage: CYCLE_DETAILS_DEFAULT_PARAMS[PARAMS.PAGE],
      newSearch: searchTerm,
    });
    setPage(CYCLE_DETAILS_DEFAULT_PARAMS[PARAMS.PAGE]);
    setSearch(searchTerm);
  };

  const onLoadMore = () => {
    setPage(page + 1);
    handleGetEmployees({
      newPage: page + 1,
      newSearch: search,
      isLoadMore: true,
    });
  };

  const onGetEmployeeDetails = employeeId => {
    return http.get(api_cycle_user_reviewers(cycleId, employeeId));
  };

  const renderReportHeader = () => {
    return (
      <div className={classes.headerWrapper}>
        <div className={classes.titleWrapper}>
          <TextBoxWithTooltip variant="h4" text={title} />
          <StatusIndicator status={cycleStatus} />
        </div>
        <div className={classes.headerInfo}>
          <Typography variant="subtitle2">{`${users_count} ${translations.employees}`}</Typography>
          <div className={classes.dot} />
          <Typography variant="body2">{`${formatDate(
            start_at,
            START_END_DATE_FORMAT
          )} - ${formatDate(end_at, START_END_DATE_FORMAT)}`}</Typography>
        </div>
      </div>
    );
  };

  const renderEmployee = user => {
    return (
      <UserAvatar
        labelClass={classes.employeeLabel}
        user={user}
        captionDescription={user.position ? user.position.name : '-'}
        caption
        withDescription
        medium
      />
    );
  };

  const renderActions = (user, hasActions) => {
    return (
      <div className={classes.employeeTableActions}>
        {hasActions && (
          <ActionButton
            type={ACTION_BUTTON_TYPES.REPORT}
            tooltipText={translations.viewReport}
            onClickHandler={() =>
              navigate(`/people/${user.id}/review-cycles`, {
                state: { cycleId },
              })
            }
          />
        )}
      </div>
    );
  };

  const employeeTableData = prepareEmployeesTableData(
    employees?.results,
    status,
    auth,
    accessControl,
    renderEmployee,
    renderActions
  );

  useEffect(() => {
    getCycleData();

    return () => {
      dispatch(clearCycle());
    };
  }, [dispatch, getCycleData]);

  return (
    !isInitialLoad && (
      <PageContainer
        {...rest}
        translations={translations}
        headerProps={{
          renderTitle: renderReportHeader,
        }}
        auth={auth}
        accessControl={accessControl}
        navigate={navigate}
        shouldPassProps={false}
        isFullWidthContent
      >
        <div>
          <div className={classes.divider} />
          <SectionTitle
            className={classes.labelSpacing}
            variant="h5"
            title={translations.timeline}
          />
          <CycleTimeline
            className={classes.sectionSpacing}
            steps={timelineStats}
          />
          <SectionTitle
            className={classes.labelSpacing}
            variant="h5"
            title={translations.cycleStatuses}
          />
          <CycleStepsStats
            className={classes.sectionSpacing}
            steps={timelineStats}
            hasEmailStep={hasEmailStep}
          />
          <SectionTitle
            className={classes.employeeSectionTitle}
            variant="h5"
            title={translations.employees}
          />
          <Typography
            className={classes.employeesTableDescription}
            variant="body2"
          >
            {translations.tableDescription}
          </Typography>
          <Search
            className={classes.search}
            placeholder={translations.searchPlaceholder}
            value={search}
            onChange={onSearch}
          />
          {isArray(employees.results) && !isArrayEmpty(employees.results) && (
            <CollapsableTable
              headerClass={classes.tableHeader}
              translations={translations.table}
              headerActions={employeesTableHeaderActions}
              columns={employeesTableHeaders}
              rows={employeeTableData}
              detailsComponent={CycleReviewers}
              detailsComponentProps={{ allSteps, reviewerStatuses }}
              hasLoadMore={hasLoadMoreEmployees}
              onGetRowDetails={onGetEmployeeDetails}
              onLoadMore={onLoadMore}
            />
          )}
          <NotificationCard
            content={translations.noSearchResults}
            shouldFade={
              isArray(employees.results) && isArrayEmpty(employees.results)
            }
            shouldFadeOutInstant
          />
        </div>
      </PageContainer>
    )
  );
};

CycleDetailsPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(CycleDetailsPage);
